import type { ReactNode } from "react";
import { BookingConsentAsk } from "./shared/consent/booking-consent-ask";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { I18nextProvider, useTranslation } from "react-i18next";
import { BookingProvider } from "@majourneys/booking";
import { PEWTER_BOOKING_ALL } from "./lib/booking-skin";
import { getI18n, type SupportedLocale } from "./lib/i18n";
import { SSR_LANG_META_NAME } from "./lib/ssr-lang";
import { api } from "./lib/api";
import { BucketProvider } from "./features/bucket/state/bucket-context";
import { SavedProvider } from "./features/saved/state/saved-context";
import { SavedStaysProvider } from "./features/saved/state/saved-stays";
/**
 * From `@majourneys/currency` directly, not via ./lib/currency, so this line
 * reads as what it is: the ONE mount of the ONE context, shared by the app and
 * by every component that arrives from @majourneys/booking. A second provider
 * anywhere puts the booking flow back on a different currency from the cards.
 */
import { CurrencyProvider, fxScript, type FxSnapshot } from "@majourneys/currency";
import { apiBase } from "./lib/api-base";
import { WhatsAppConversionTracker } from "./lib/whatsapp-conversion";
import { AnalyticsIdentity } from "./shared/analytics/analytics-identity";
import { SignInWelcome } from "./features/auth/components/sign-in-welcome";
import { GuestBookingClaimer } from "./features/account/components/guest-booking-claimer";
import { TripsAccountSync } from "./features/account/state/trips-account-sync";
import { KeepBookingCard } from "./features/auth/components/keep-booking-card";
import { track } from "./shared/analytics/posthog";
// A thin wrapper: the Google Maps picker itself is a lazy chunk, fetched only
// when a transfer form renders a place field.
import { TRANSPORT_PLACE_FIELD } from "./features/transport/components/place-field-lazy";
import "./lib/tailwind.css";
import { RideThereCard } from "./features/transport/components/ride-there-card";
import { ThankYouAppBanner } from "./shared/app-promo/thank-you-app-banner";
import { parseLang } from "@majourneys/shared";

/** The help card's after-hours question opens MaJourneys AI with it already asked (round 18, item 144). */
const aiAskHref = (query: string, lang: string) =>
  `/${parseLang(lang)}/ai-travel-planner?q=${encodeURIComponent(query.slice(0, 300))}`;
import "@majourneys/auth-ui/styles.css";
import { categoryIconSrc, serviceIconSrc } from "./features/activities/lib/category-icon";

const SITE_ORIGIN =
  (import.meta.env.VITE_SITE_URL as string | undefined) ?? "https://majourneys.com";

/**
 * Emits the language the i18n provider actually rendered with. The Worker
 * (`server.ts`) compares it against the path locale before writing the
 * response to the edge cache, so wrong-language HTML can never be cached.
 */
function SsrLangMarker() {
  const { i18n: instance } = useTranslation();
  return <meta name={SSR_LANG_META_NAME} content={instance.language} />;
}

/**
 * Leaves the server's currency snapshot on the page for the browser to start
 * from (lib/fx-snapshot.ts reads it back). Inline and parser-blocking, so it
 * has run before the module scripts hydrate. The browser renders the same
 * element from the same value, so hydration sees no difference.
 */
function FxScript({ fx }: { fx: FxSnapshot | null }) {
  if (!fx) return null;
  return <script dangerouslySetInnerHTML={{ __html: fxScript(fx) }} />;
}

export function AppRoot({
  queryClient,
  lang,
  fx = null,
  children,
}: {
  queryClient: QueryClient;
  lang: SupportedLocale;
  /** What the server priced this page with. Null: the browser works it out after paint, as before. */
  fx?: FxSnapshot | null;
  children: ReactNode;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={getI18n(lang)}>
        <BookingProvider
          api={api}
          siteOrigin={SITE_ORIGIN}
          pewterAll={PEWTER_BOOKING_ALL}
          track={track}
          categoryIconSrc={categoryIconSrc}
          serviceIconSrc={serviceIconSrc}
          PlaceField={TRANSPORT_PLACE_FIELD}
          AccountPrompt={KeepBookingCard}
          RidePrompt={RideThereCard}
          AppPrompt={ThankYouAppBanner}
          aiAskHref={aiAskHref}
          ConsentPrompt={BookingConsentAsk}
        >
          {/* Same rates the API priced with, so the app and the site never
              quote different money for the same activity. */}
          <CurrencyProvider ratesUrl={`${apiBase()}/currency/rates`} initial={fx}>
            <FxScript fx={fx} />
            <BucketProvider>
              <SavedProvider>
                <SavedStaysProvider>
                  {children}
                  <SsrLangMarker />
                  <WhatsAppConversionTracker />
                  <AnalyticsIdentity />
                  <SignInWelcome />
                  <GuestBookingClaimer />
                  <TripsAccountSync />
                </SavedStaysProvider>
              </SavedProvider>
            </BucketProvider>
          </CurrencyProvider>
        </BookingProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}
